import { useMemo, useCallback } from 'react'

/**
 * Hook for managing ticket permission checks
 * Centralizes all permission logic for ticket operations
 */
export function useTicketPermissions(ticket, displayTicket, user) {
  // Check if user can add notes (has Application.UserNotes role or is admin)
  const canAddNotes = useMemo(() => {
    const hasUserNotesRole = user?.application_roles?.some(role =>
      role.toLowerCase() === 'application.usernotes'
    )
    const hasTokenAdminRole = user?.roles?.some(role =>
      role.toLowerCase() === 'agentflow.admin'
    )
    const hasApplicationAdminRole = user?.application_roles?.some(role =>
      role.toLowerCase() === 'application.admin'
    )
    return hasUserNotesRole || hasTokenAdminRole || hasApplicationAdminRole
  }, [user?.application_roles, user?.roles])

  // Check if user is admin (using same logic as Layout.jsx)
  const isAdmin = useMemo(() => {
    const hasTokenAdminRole = user?.roles?.some(role =>
      role.toLowerCase() === 'agentflow.admin'
    )
    const hasApplicationAdminRole = user?.application_roles?.some(role =>
      role.toLowerCase() === 'application.admin'
    )
    return hasTokenAdminRole || hasApplicationAdminRole
  }, [user?.roles, user?.application_roles])

  // Check if user is an agent admin for this ticket's agent type
  const isAgentAdmin = useMemo(() => {
    const agentType = displayTicket?.type || ticket?.type
    if (!agentType) return false
    const role = `application.agentadmin.${agentType}`.toLowerCase()
    return user?.application_roles?.some(r => r.toLowerCase() === role)
  }, [user?.application_roles, ticket?.type, displayTicket?.type])

  const canRespond = useCallback(() => {
    // Don't allow responses if viewing a parent ticket with an active child
    if (displayTicket && ticket && displayTicket.id === ticket.id && ticket.active_child) {
      return false
    }

    // Don't allow responses if viewing a non-active child ticket
    if (displayTicket && ticket && displayTicket.id !== ticket.id) {
      if (ticket.active_child !== displayTicket.id) {
        return false
      }
    }

    // Check if user is an admin (from token OR application roles)
    const hasTokenAdminRole = user?.roles?.some(role => role.toLowerCase() === 'agentflow.admin')
    const hasApplicationAdminRole = user?.application_roles?.some(role => role.toLowerCase() === 'application.admin')
    const userIsAdmin = hasTokenAdminRole || hasApplicationAdminRole

    // Admin users can respond to any ticket in waiting, error, or complete state
    if (userIsAdmin && (displayTicket?.status === 'waiting' || displayTicket?.status === 'error' || displayTicket?.status === 'complete' || displayTicket?.status === 'completed')) {
      return true
    }

    // Agent admins can respond to waiting/error tickets (but NOT completed)
    if (isAgentAdmin && (displayTicket?.status === 'waiting' || displayTicket?.status === 'error')) {
      return true
    }

    // Check if user is assigned or in group_chat (case-insensitive)
    const userEmail = user?.email?.toLowerCase()
    const isAssigned = userEmail && displayTicket?.assigned_to?.toLowerCase() === userEmail
    const isInGroup = userEmail && displayTicket?.group_chat?.some(email => email.toLowerCase() === userEmail)

    // Regular users can respond if:
    // 1. They are logged in
    // 2. Ticket is assigned to them OR they're in the group_chat
    // 3. Either:
    //    a) Ticket is in error status (allow recovery from errors)
    //    b) Ticket is waiting AND role is 'user' or 'external'
    if (!user?.email || (!isAssigned && !isInGroup)) {
      return false
    }

    // Allow response to error state tickets
    if (displayTicket?.status === 'error') {
      return true
    }

    // For waiting status, check role
    if (displayTicket?.status === 'waiting' && (displayTicket?.role === 'user' || displayTicket?.role === 'external')) {
      return true
    }

    return false
  }, [ticket, displayTicket, user, isAgentAdmin])

  const getReplyDisabledMessage = useCallback(() => {
    if (!displayTicket) return ''

    // If viewing parent ticket with an active child, show message to switch to child
    if (displayTicket && ticket && displayTicket.id === ticket.id && ticket.active_child) {
      return `Child ${ticket.active_child} is processing this ticket, please use tabs above`
    }

    // If viewing a non-active child ticket, show disabled message
    if (displayTicket && ticket && displayTicket.id !== ticket.id) {
      if (ticket.active_child !== displayTicket.id) {
        return 'Cannot respond to inactive child ticket'
      }
    }

    if (displayTicket.status === 'complete' || displayTicket.status === 'completed') {
      if (isAgentAdmin) {
        return 'Agent admins cannot respond to completed tickets'
      }
      return 'Ticket is completed'
    }

    if (displayTicket.status === 'processing') {
      return 'Agent is processing...'
    }

    if (displayTicket.role === 'agent') {
      return 'Waiting for agent response'
    }

    // Check if user is in group_chat or assigned (case-insensitive)
    const userEmail = user?.email?.toLowerCase()
    const isAssigned = userEmail && displayTicket.assigned_to?.toLowerCase() === userEmail
    const isInGroup = userEmail && displayTicket?.group_chat?.some(email => email.toLowerCase() === userEmail)

    if (displayTicket.assigned_to && !isAssigned && !isInGroup) {
      const isExternalAssignment = displayTicket.role === 'external'
      if (isExternalAssignment) {
        return `Ticket assigned externally to ${displayTicket.assigned_to}`
      } else {
        return `Ticket assigned to ${displayTicket.assigned_to}`
      }
    }

    if (!user?.email) {
      return 'Please log in to respond'
    }

    // If role is 'external' but user has access, allow response
    if (displayTicket.role === 'external' && (isAssigned || isInGroup)) {
      return 'Type your response here...'
    }

    return 'Waiting for response'
  }, [ticket, displayTicket, user, isAgentAdmin])

  const canDelete = useCallback(() => {
    const hasTokenAdminRole = user?.roles?.some(role =>
      role.toLowerCase() === 'agentflow.admin'
    )
    const hasApplicationAdminRole = user?.application_roles?.some(role =>
      role.toLowerCase() === 'application.admin'
    )
    const userIsAdmin = hasTokenAdminRole || hasApplicationAdminRole
    const isCreator = user?.email && ticket?.created_by === user.email
    return isCreator || userIsAdmin
  }, [ticket, user])

  const canArchive = useCallback(() => {
    // Can archive if ticket is not already archived and user has access
    if (ticket?.archived) return false
    const userEmail = user?.email?.toLowerCase()
    const isCreator = userEmail && ticket?.created_by?.toLowerCase() === userEmail
    const isAssigned = userEmail && ticket?.assigned_to?.toLowerCase() === userEmail
    const isInGroup = userEmail && ticket?.group_chat?.some(email => email.toLowerCase() === userEmail)
    const hasTokenAdminRole = user?.roles?.some(role =>
      role.toLowerCase() === 'agentflow.admin'
    )
    const hasApplicationAdminRole = user?.application_roles?.some(role =>
      role.toLowerCase() === 'application.admin'
    )
    const userIsAdmin = hasTokenAdminRole || hasApplicationAdminRole
    return isCreator || isAssigned || isInGroup || userIsAdmin
  }, [ticket, user])

  const canPermanentlyDelete = useCallback(() => {
    // Only admins can permanently delete, and only if ticket is archived
    if (!ticket?.archived) return false
    const hasTokenAdminRole = user?.roles?.some(role =>
      role.toLowerCase() === 'agentflow.admin'
    )
    const hasApplicationAdminRole = user?.application_roles?.some(role =>
      role.toLowerCase() === 'application.admin'
    )
    const userIsAdmin = hasTokenAdminRole || hasApplicationAdminRole
    return userIsAdmin
  }, [ticket, user])

  return {
    isAdmin,
    isAgentAdmin,
    canAddNotes,
    canRespond,
    canDelete,
    canArchive,
    canPermanentlyDelete,
    getReplyDisabledMessage
  }
}

export default useTicketPermissions
